/**
 * LegalProComparisonTable
 * ───────────────────────
 * Side-by-side comparison between generic ChatGPT (or any non-vertical AI)
 * and NEURACOM Legal Pro. Lives below the Pricing grid to anchor the value
 * argument: same monthly spend, radically better legal output.
 *
 * Layout strategy:
 * - Mobile (<sm): compact 3-col grid with reduced padding, tighter text,
 *   shortened subtitles. Icons stay 24px so tap targets remain comfortable.
 * - Desktop (≥sm): the original 1.4fr / 1fr / 1fr layout with generous padding.
 *
 * Columns: Feature / Generic AI / NEURACOM Legal Pro
 * Categories: Citations · Privacy · Workflow · Cost
 */
import React from "react";
import { Check, X, Scale } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const ROWS = [
  // ─── Citazioni e ricerca giurisprudenziale ───
  { category: "citations", key: "row_jurisprudence", generic: false, neuracom: true },
  { category: "citations", key: "row_kb", generic: false, neuracom: true },
  { category: "citations", key: "row_auto_cite", generic: false, neuracom: true },
  { category: "citations", key: "row_hallucinations", generic: "warn", neuracom: true },
  // ─── Privacy & compliance ───
  { category: "privacy", key: "row_eu_hosted", generic: false, neuracom: true },
  { category: "privacy", key: "row_gdpr", generic: "warn", neuracom: true },
  { category: "privacy", key: "row_no_training", generic: false, neuracom: true },
  // ─── Workflow legale ───
  { category: "workflow", key: "row_templates", generic: false, neuracom: true },
  { category: "workflow", key: "row_legal_register", generic: "warn", neuracom: true },
  { category: "workflow", key: "row_doc_analysis", generic: false, neuracom: true },
  // ─── Cost ───
  { category: "cost", key: "row_price", generic: "20", neuracom: "149" },
  { category: "cost", key: "row_value", generic: "neutral", neuracom: true },
];

const Cell = ({ value }) => {
  if (value === true)
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 sm:h-7 sm:w-7">
        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-500/15 text-red-300 sm:h-7 sm:w-7">
        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </span>
    );
  if (value === "warn")
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500/15 text-[11px] font-bold text-yellow-300 sm:h-7 sm:w-7 sm:text-xs">
        !
      </span>
    );
  return (
    <span className="inline-flex items-center font-mono text-xs font-bold text-white sm:text-sm">
      {value === "20" ? "$20" : value === "149" ? "€149" : value}
    </span>
  );
};

export function LegalProComparisonTable() {
  const { t } = useI18n();

  return (
    <section className="mx-auto mt-16 max-w-5xl px-3 sm:mt-24 sm:px-6 lg:px-10" data-testid="legal-comparison-table">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#F4C955]">
          <Scale className="h-3 w-3" />
          {t("comparison.pretitle")}
        </span>
        <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
          {t("comparison.title")}
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-400 sm:mt-4 sm:text-base">
          {t("comparison.subtitle")}
        </p>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/50 backdrop-blur sm:mt-12">
        {/* Header row — compact on mobile, generous on desktop */}
        <div className="grid grid-cols-[1.3fr,1fr,1fr] border-b border-slate-700 bg-slate-900/80 sm:grid-cols-[1.4fr,1fr,1fr]">
          <div className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 sm:px-6 sm:py-4">
            {t("comparison.col_feature")}
          </div>
          <div className="border-l border-slate-700 px-2 py-3 text-center sm:px-6 sm:py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              {t("comparison.col_generic")}
            </div>
            <div className="mt-1 hidden font-mono text-[10px] text-slate-400 sm:block sm:text-xs">
              {t("comparison.generic_subtitle")}
            </div>
          </div>
          <div className="border-l border-[#D4A937]/30 bg-[#D4A937]/5 px-2 py-3 text-center sm:px-6 sm:py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F4C955]">
              {t("comparison.col_neuracom")}
            </div>
            <div className="mt-1 hidden font-mono text-[10px] text-[#D4A937] sm:block sm:text-xs">
              {t("comparison.neuracom_subtitle")}
            </div>
          </div>
        </div>

        {/* Category groups */}
        {["citations", "privacy", "workflow", "cost"].map((cat) => (
          <div key={cat}>
            <div className="border-b border-slate-800 bg-slate-900/30 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 sm:px-6 sm:py-2.5">
              {t(`comparison.cat_${cat}`)}
            </div>
            {ROWS.filter((r) => r.category === cat).map((row) => (
              <div
                key={row.key}
                className="grid grid-cols-[1.3fr,1fr,1fr] border-b border-slate-800/80 last:border-b-0 sm:grid-cols-[1.4fr,1fr,1fr]"
                data-testid={`comparison-${row.key}`}
              >
                <div className="px-3 py-3 text-xs leading-snug text-slate-200 sm:px-6 sm:py-4 sm:text-sm sm:leading-normal">
                  {t(`comparison.${row.key}`)}
                </div>
                <div className="flex items-center justify-center border-l border-slate-800 px-2 py-3 sm:px-6 sm:py-4">
                  <Cell value={row.generic} />
                </div>
                <div className="flex items-center justify-center border-l border-[#D4A937]/15 bg-[#D4A937]/[0.03] px-2 py-3 sm:px-6 sm:py-4">
                  <Cell value={row.neuracom} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ROI bottom-line */}
      <div
        className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-center sm:mt-8 sm:p-6"
        data-testid="comparison-roi"
      >
        <p className="font-display text-base font-extrabold text-emerald-300 sm:text-xl">
          {t("comparison.roi_headline")}
        </p>
        <p className="mt-2 text-xs text-slate-400 sm:text-sm">{t("comparison.roi_body")}</p>
      </div>
    </section>
  );
}
